import { Cell, CellState, ImageShape } from "@maxgraph/core";
import { DiagramCardData } from "@nms-angular-toolkit/nms-diagram-card";
import { Transmission } from "@nms-ng2/app/modules/topology/links/links.model";
import { Identifier } from "@nms-ng2/app/shared/models/basic.models";
import { SimpleLayoutElement } from "@nms-ng2/app/shared/services/layout-manager/layout-manager.models";

/**
 * Mapeamento de LinkElement (parcial) por Transmission (i.e. RX, TX).
 */
export type TransmissionLinkMap = {
    [key in Transmission]?:  Partial<LinkElement>;
}

/**
 * Mapeamento de TransmissionMap por HorizontalPosition (i.e. left, right).
 */
export type TransmissionLinkPositionMap = {
    [key in HorizontalPosition]?: TransmissionLinkMap;
}

/**
 * Mapeamento de OSC por HorizontalPosition (i.e. left, right).
 */
export type OscPositionMap = {
    [key in HorizontalPosition]?: OscElement;
}

/**
 * Mapeamento de amplificador por CircuitDirection.
 */
export type AmplifierDirectionMap = {
    [key in CircuitDirection]?: AmplifierElement;
}

/**
 * Mapeamento que define quais transmissões se aplicam de
 * acordo com o posicionamento horizontal e vertical delas.
 */
export type TransmissionPositionMap = {
    [key in HorizontalPosition]: {
        [key in VerticalPosition]: Transmission;
    };
}


/**
 * Interface genérica para um elemento do circuito.
 *
 * id:      Identificador do elemento.
 * element: Elemento de layout correspondente a este elemento do circuito.
 * state:   Estado do elemento do circuito
 */
export interface CircuitElement {
    id?: Identifier;
    element?: SimpleLayoutElement;
    state?: CircuitElementState;
    getCustomTooltip?: Function;
    buildPopover?: Function;
    popoverData?: DiagramCardData;
}

/**
 * Interface que representa o circuito completo.
 *
 * aEnd:          Endpoint A do circuito.
 * zEnd:          Endpoint Z do circuito.
 * intermediates: Lista de equipamentos intermediários (pode ser vazia).
 * links:         Links externos que ligam os equipamentos (endpoints ou intermediários).
 */
export interface CircuitDiagram {
    aEnd: EndpointDeviceElement<EndpointType.A_END>;
    zEnd: EndpointDeviceElement<EndpointType.Z_END>;
    intermediates: IntermediateDeviceElement[];
    links?: LinkElement[];
    showExternalLinks?: boolean;
}

/**
 * Interface que representa um equipamento do Circuito (endpoint ou intermediário).
 *
 * name:                 Nome do equipamento
 * transmissionPosition: Mapeamento do posicionamento das transmissões (RX/TX) deste equipamento.
 * amplifiers:           Lista de amplificadores presentes no equipamento.
 * type:                 Tipo de equipamento que indica se é um endpoint ou um intermediário (que pode ser menor ou completo).
 * showInternalLinks:    Flag que indica se os links internos deste equipamento devem ser exibidos.
 */
export interface DeviceElement extends CircuitElement {
    name?: string;
    transmissionPosition?: TransmissionLinkPositionMap;
    amplifiers?: AmplifierDirectionMap;
    type?: DeviceType;
    showInternalLinks?: boolean;
}

/**
 * Interface que representa um equipamento que é endpoint do Circuito.
 *
 * endpointType: Tipo que indica se este endpoint é um A-END ou um Z-END.
 * transponders: Lista de transponders presentes no equipamento.
 * mux:          Mux do equipamento.
 * osc:          Gerência OSC associada a este equipamento.
 */
export interface EndpointDeviceElement<T extends EndpointType> extends DeviceElement {
    endpointType: T;
    transponders: TransponderElement[];
    mux?: MuxElement;
    osc?: OscElement;
}

/**
 * Interface que representa um equipamento intermediário do Circuito.
 *
 * oscPosition: Mapeamento do posicionamento das Gerências OSC deste equipamento.
 */
export interface IntermediateDeviceElement extends DeviceElement {
    oscPosition?: OscPositionMap;
}

/**
 * Interface de marcação que representa um transceiver.
 */
export interface TransceiverElement extends CircuitElement { }

/**
 * Interface que representa um transponder.
 *
 * multirate:    Indica qual a configuração de multirate (i.e. 1x100GbE, 2x100GbE, 3x100GbE, 4x100GbE) do transponder.
 * transceivers: Lista de transceivers.
 */
export interface TransponderElement extends CircuitElement {
    multirate: TransponderMultirate;
    transceivers?: TransceiverElement[];
}

/**
 * Interface de marcação que representa um Mux/Demux.
 */
export interface MuxElement extends CircuitElement { }

/**
 * Interface que representa um amplificador.
 *
 * name:      Nome do amplificador.
 * type:      Indica o tipo do amplificador (Pre-Amp ou Booster).
 * direction: Direção do circuito, indica se está no caminho que vai até o Z-END ou até o A-END.
 */
export interface AmplifierElement extends CircuitElement {
    name?: string;
    type: AmplifierType;
    direction: CircuitDirection;
}

/**
 * Interface que representa uma gerência OSC.
 *
 * name: Nome do elemento OSC.
 */
export interface OscElement extends CircuitElement {
    name: string;
}

/**
 * Interface que representa um Link interno (entre Transponders/Muxes/Amplificadores/Equipamentos).
 *
 * source: Identificador do elemento de origem do link interno.
 * target: Identificador do elemento de destino do link interno.
 */
export interface InternalLinkElement extends CircuitElement {
    source: Identifier;
    target: Identifier;
}

/**
 * Interface que representa um ponto de conexão para um link.
 *
 * deviceIdentifier: Identificador do equipamento que receberá a ligação.
 * transmission:     Transmissão utilizada para a ligação (i.e. RX/TX).
 */
export interface DeviceLinkPoint {
    deviceIdentifier: Identifier;
    transmission: Transmission;
}

/**
 * Interface que representa um Link.
 *
 * source:    Origem do link.
 * target:    Destino do link.
 * direction: Direção do link em relação ao circuito.
 * name:      Nome do link.
 * canRemove: Flag que indica se o link pode ser removido pelo diagrama.
 */
export interface LinkElement extends CircuitElement {
    source: DeviceLinkPoint;
    target: DeviceLinkPoint;
    direction: CircuitDirection;
    name?: string;
    canRemove: boolean;
    onRemove?: Function;
}

/**
 * Tipos que definem os posicionamentos horizontais e verticais para o diagrama.
 */
export type HorizontalPosition = "left" | "right";
export type VerticalPosition = "top" | "bottom";

/**
 * Enum que define os tipos possíveis para os amplificadores.
 */
export enum AmplifierType {
    BOOSTER = "BOOSTER",
    PRE_AMP = "PRE_AMP"
}

/**
 * Enum que define o tipo de endpoint, se é um A-END ou Z-END.
 */
export enum EndpointType {
    A_END = "A_END",
    Z_END = "Z_END"
}

/**
 * Enum que define os valores de multirate possíveis para os transponders.
 */
export enum TransponderMultirate {
    MULTIRATE_1X_100 = "1x100GbE",
    MULTIRATE_2X_100 = "2x100GbE",
    MULTIRATE_3X_100 = "3x100GbE",
    MULTIRATE_4X_100 = "4x100GbE"
}

/**
 * Indica se está no caminho que vai até o Z-END ou até o A-END
 */
export enum CircuitDirection {
    TOWARDS_Z_END = "TOWARDS_Z_END",
    TOWARDS_A_END = "TOWARDS_A_END"
}

/**
 * Tipos de elementos do circuito.
 */
export enum CircuitElementType {
    DEVICE      = "DEVICE",
    MUX         = "MUX",
    TRANSPONDER = "TRANSPONDER",
    OSC         = "OSC",
    AMPLIFIER   = "AMPLIFIER",
    LINK        = "LINK"
}

/**
 * Enum que define os tipos de equipamento, podendo ser endpoint,
 * intermediários menores ou intermediário completos.
 */
export enum DeviceType {
    END               = "END",
    INTERMEDIATE_FULL = "INTERMEDIATE_FULL",
    INTERMEDIATE_HALF = "INTERMEDIATE_HALF"
}

/**
 * Enum que define os estados possíveis para os elementos do circuito.
 */
export enum CircuitElementState {
    INITIAL        = "INITIAL",
    NOT_CONFIGURED = "NOT_CONFIGURED",
    PRE_CONFIGURED = "PRE_CONFIGURED",
    CONFIGURED     = "CONFIGURED"
}

/**
 * Tipos de botões do diagram.
 */
export enum CircuitButton {
    DELETE = "DELETE"
}

/**
 * Dados dos botões do diagrama.
 */
export interface CircuitButtonData {
    imagePath: string;
    width:     number;
    height:    number;
    scale:     number;
}

/**
 * Mapeamento de CircuitButtonData por CircuitButton.
 */
export type CircuitButtonMap = {
    [key in CircuitButton]: CircuitButtonData;
}

/**
 * Customização para estado da célula do maxgraph.
 */
export class CustomCellState extends CellState {
    customBuildCallback?: Function;
    deleteControl: ImageShape;
    cell: CustomCell;
}

/**
 * Customização para a célula do maxgraph.
 */
export class CustomCell extends Cell {
    customName: string;
    customBuildCallback?: Function;
    hasAnimation: boolean;
    setCustomStyle: Function;
    getCustomTooltip: Function;
    buildPopover?: Function;
    popoverData?: DiagramCardData;
}

/**
 * Modelo de dados que define um item da legenda.
 */
export interface DiagramLegendItem {
    imagePath: string;
    labelKey: string;
    customStyle: string;
}

/**
 * Modelo de dados que define um grupo da legenda.
 */
export interface DiagramLegendGroup {
    title: string;
    items: DiagramLegendItem[];
}

/**
 * Tipo union que define os valores de "Source" e "Target".
 */
export type SourceOrTarget = "source" | "target";

/**
 * Modelo de dados que define os pontos que terão seu posicionamento alterado
 * a partir da aplicação de um offset.
 */
export interface PointsReposition {
    /** Valor de deslocamento a ser aplicado */
    offset: number;
    /** Índices dos pontos a serem reposicionados */
    points: Array<number>;
    /** Define se o reposicionamento deve ser feito a partir do source ou do target */
    basedOn?: SourceOrTarget;
}
